import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AttachmentOwnerType,
  DEFAULT_ROBOT_RETURN_LOCATION,
  DEPLOYMENT_RETURN_REASON_LABELS,
  DeploymentReturnReason,
  ROBOT_RETURN_LOCATION_CHOICES,
  ROBOT_RETURN_LOCATION_LABELS,
  UserRole,
  createsReturnMaintenanceOrder,
  getHolidayName,
  isKoreanHoliday,
  type RobotReturnDisposition,
  type RobotReturnLocation,
} from '@bhops/shared';
import { deploymentsApi } from '@/api/deployments';
import { uploadAttachment } from '@/api/attachments';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Select } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { useAuthStore } from '@/stores/auth';
import { canEditAny } from '@/permissions';
import { kstTodayYmd, kstYmd, ymdToIso } from '@/lib/format-date';

interface Props {
  open: boolean;
  onClose: () => void;
  robotId: string;
}

export function ReturnRobotModal({ open, onClose, robotId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  // 권한 통합(오너 결정 2026-08-27) — 종전엔 폐기(RETIRE)가 ADMIN 전용이라
  // 비관리자에게 옵션을 감췄다(열면 항상 403 이었다). 서버 세 겹(CASL · 상태머신
  // isRoleAllowedForTransition · 서비스 가드)이 모두 열렸으므로 화면도 연다.
  // 이름은 유지한다(호출부가 여럿) — 뜻은 이제 '폐기를 고를 수 있는 사람'이다.
  const isAdmin = useAuthStore((s) => (s.user ? canEditAny(s.user.role) : false));

  const active = useQuery({
    queryKey: ['deployments', 'active', robotId],
    queryFn: () => deploymentsApi.list({ robotId, activeOnly: true, pageSize: 1 }),
    enabled: open,
  });

  // 추가수정 2차(26-07-27) §2 — '오늘' 기본값을 KST 로(UTC 기준이면 한국
  // 오전 9시 전에 어제 날짜가 기본값으로 들어갔다).
  const [returnedAt, setReturnedAt] = useState<string>(() => kstTodayYmd());
  const [returnReason, setReturnReason] = useState<DeploymentReturnReason>(
    DeploymentReturnReason.CONTRACT_END,
  );
  const [disposition, setDisposition] = useState<RobotReturnDisposition>('MAINTENANCE');
  // 개선 9건 차수1(26-08-04) 결정 ①A — 회수 장소. 기본값이 A/S센터라 기존 습관이
  // 그대로 통한다(아무것도 고르지 않으면 종전과 같은 결과).
  const [returnLocation, setReturnLocation] = useState<RobotReturnLocation>(
    DEFAULT_ROBOT_RETURN_LOCATION,
  );
  const [notes, setNotes] = useState<string>('');
  // 계획서(2026-08-27) 요구 3 — 회수하면서 바로 사진을 올린다(선택).
  // 사진은 **회수 기록(Deployment)** 에 붙는다 — 고객사에 통째로 붙이면 여러 번
  // 회수한 고객사에서 "어느 로봇의 어느 회수인지"를 나중에 가를 수 없다.
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReturnedAt(kstTodayYmd());
      setReturnReason(DeploymentReturnReason.CONTRACT_END);
      setPhotos([]);
      setPhotoError(null);
      setDisposition('MAINTENANCE');
      setReturnLocation(DEFAULT_ROBOT_RETURN_LOCATION);
      setNotes('');
      setError(null);
    }
  }, [open, robotId]);

  const deployment = active.data?.items[0];
  // 결정 ⑧ — 행사·전시 반납은 정비가 아니라 출고 대기로 돌아간다. 처분(정비/폐기)
  // 선택은 고객사 배치 회수에만 의미가 있어 그때만 노출한다.
  const isCustomerReturn = deployment?.kind === 'CUSTOMER';
  const customerId = deployment?.customerId ?? null;

  // 결정 ⑩ — 마지막 운영 로봇이면 고객사가 자동 종료된다. 회수 버튼을 누르기
  // 전에 알려주려고 같은 고객사의 활성 배치 수를 먼저 센다.
  const siblings = useQuery({
    queryKey: ['deployments', 'active-by-customer', customerId],
    queryFn: () =>
      deploymentsApi.list({ customerId: customerId!, activeOnly: true, pageSize: 100 }),
    enabled: open && isCustomerReturn && !!customerId,
  });
  // SWAP_DEFECT(고장 교체)는 백엔드가 자동 종료를 억제한다 — 경고도 같이 접는다.
  const isLastRobot =
    isCustomerReturn &&
    returnReason !== DeploymentReturnReason.SWAP_DEFECT &&
    siblings.data !== undefined &&
    siblings.data.items.length <= 1;

  // 개선 9건 차수1(26-08-04) 결정 ①A — 장소 선택은 **정비 입고**에만 의미가 있다.
  // 폐기(RETIRE)는 창고 고정, 행사·전시 반납도 창고 고정이라 그때는 감춘다
  // (서버 resolveReturnTransition 과 같은 규칙 — 화면이 못 보내는 값을 보이지 않는다).
  const showLocation = isCustomerReturn && disposition === 'MAINTENANCE';
  // 정비 지시가 만들어지는 장소인가 — 판정은 shared 정본(서버와 같은 함수).
  const makesServiceOrder = createsReturnMaintenanceOrder(returnLocation);

  const ret = useMutation({
    mutationFn: () => {
      if (!deployment) throw new Error('활성 배치가 없습니다.');
      return deploymentsApi.return(deployment.id, {
        returnedAt: ymdToIso(returnedAt),
        notes: notes || undefined,
        returnReason,
        disposition: isCustomerReturn ? disposition : undefined,
        // 개선 9건 차수1(26-08-04) — 장소는 화면이 그것을 물었을 때만 보낸다.
        // 안 물었는데 보내면 서버가 무시하는 값을 화면이 약속하는 셈이 된다.
        returnLocation: showLocation ? returnLocation : undefined,
      });
    },
    onSuccess: async (_res, _vars) => {
      // 사진은 **회수가 성공한 뒤에** 올린다. 먼저 올리면 회수가 실패했을 때
      // 주인 없는 사진이 남는다(그 배치는 아직 회수 기록이 아니다).
      //
      // ⚠ 업로드가 실패해도 **회수는 되돌리지 않는다** — 로봇은 이미 돌아왔고
      //   그 사실이 사진보다 중요하다. 대신 실패를 조용히 넘기지 않고 화면에
      //   남겨, 운영자가 서류 탭에서 다시 올릴 수 있게 안내한다.
      const failedPhotos: string[] = [];
      if (photos.length > 0 && deployment) {
        for (const file of photos) {
          try {
            await uploadAttachment(file, {
              ownerType: AttachmentOwnerType.DEPLOYMENT_RETURN,
              ownerId: deployment.id,
            });
          } catch {
            failedPhotos.push(file.name);
          }
        }
        if (failedPhotos.length > 0) {
          setPhotoError(
            `회수는 됐지만 사진 ${failedPhotos.length}장을 올리지 못했습니다` +
              `(${failedPhotos.join(', ')}). 고객사 서류 탭의 '회수 로봇 사진'에서 다시 올릴 수 있습니다.`,
          );
        }
        qc.invalidateQueries({ queryKey: ['attachments'] });
      }
      qc.invalidateQueries({ queryKey: ['robot', robotId] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      // Robot status changed (DEPLOYED → IN_STORAGE/etc) → lifecycle
      // pipeline counts on RobotsListPage are stale. Invalidate ALL
      // stage count queries so the pipeline reflects the move
      // immediately rather than waiting for the 60s staleTime.
      qc.invalidateQueries({ queryKey: ['robots-count'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robotId] });
      qc.invalidateQueries({ queryKey: ['deployments'] });
      // 고객사가 종료됐을 수 있으므로 고객사 화면 캐시도 함께 무효화.
      qc.invalidateQueries({ queryKey: ['customer-master'] });
      qc.invalidateQueries({ queryKey: ['active-customers'] });
      toast.success(
        isLastRobot
          ? '로봇이 회수되었습니다. 이 고객사의 마지막 로봇이라 고객사가 종료 처리됩니다.'
          : '로봇이 회수되었습니다.',
        '회수 완료',
      );
      // 사진 실패 안내가 있으면 창을 열어 둔다 — 닫아 버리면 그 안내를 아무도 못 본다.
      if (failedPhotos.length > 0) return;
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    ret.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="로봇 회수"
      width={440}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="return-form"
            variant="danger"
            disabled={ret.isPending || !deployment}
          >
            {ret.isPending ? '처리 중…' : '회수'}
          </Button>
        </>
      }
    >
      <form id="return-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        {active.isLoading ? (
          <div className="kuun-caption">활성 배치 확인 중…</div>
        ) : !deployment ? (
          <div style={{ color: 'var(--fg-danger)' }}>
            현재 활성 배치가 없습니다. 배치를 먼저 생성해 주세요.
          </div>
        ) : (
          <div
            style={{
              padding: 10,
              marginBottom: 14,
              background: 'var(--bg-sunken)',
              borderRadius: 'var(--r-card)',
              fontSize: 13,
            }}
          >
            <div>
              배치 ID: <span className="kuun-mono">{deployment.id.slice(-8)}</span>
            </div>
            <div className="kuun-caption" style={{ marginTop: 2 }}>
              {/* 추가수정 2차(26-07-27) §2 — 브라우저 로캘 대신 KST 고정. */}
              배치 일자: {kstYmd(deployment.deployedAt) ?? '–'}
            </div>
            {/* 개선 9건 차수1(26-08-04) 결정 ①A — 안내문을 장소에 연동한다.
                연구소·창고로 회수하면 정비 지시가 만들어지지 않으므로, 회수하는
                사람이 **그 자리에서** 뒷정리 책임을 알아야 한다(계획서 §1 고지와
                같은 문장). '정비 중' 수치에는 여전히 포함된다. */}
            <div className="kuun-caption" style={{ marginTop: 4 }} data-testid="return-notice">
              {!isCustomerReturn
                ? '행사 반납은 출고 대기로 돌아갑니다.'
                : !showLocation
                  ? '폐기 처리된 로봇은 창고로 돌아갑니다.'
                  : `회수한 로봇은 정비 상태로 ${ROBOT_RETURN_LOCATION_LABELS[returnLocation]}에 들어갑니다.`}
            </div>
            {showLocation && !makesServiceOrder && (
              <div className="kuun-caption" style={{ marginTop: 4, fontWeight: 600 }}>
                정비 지시가 만들어지지 않습니다. 분석이나 보관이 끝나면 로봇 편집에서 상태를 정리해
                주세요.
              </div>
            )}
          </div>
        )}

        {/* 추가수정 2차(26-07-27) §2 — 공휴일 경고 확산(결정 ③A).
            InfrastructureOrderModal 의 문구 방식을 그대로 복제. */}
        <Field
          label="회수 일자"
          htmlFor="ret-date"
          required
          hint={
            isKoreanHoliday(returnedAt)
              ? `공휴일 (${getHolidayName(returnedAt)}) 입니다`
              : undefined
          }
        >
          <Input
            id="ret-date"
            type="date"
            value={returnedAt}
            onChange={(e) => setReturnedAt(e.target.value)}
            required
          />
        </Field>

        <Field label="회수 사유" htmlFor="ret-reason" required>
          <Select
            id="ret-reason"
            value={returnReason}
            onChange={(e) => setReturnReason(e.target.value as DeploymentReturnReason)}
            required
          >
            {(Object.keys(DEPLOYMENT_RETURN_REASON_LABELS) as DeploymentReturnReason[]).map((r) => (
              <option key={r} value={r}>
                {DEPLOYMENT_RETURN_REASON_LABELS[r]}
              </option>
            ))}
          </Select>
        </Field>

        {isCustomerReturn && (
          <Field label="처분" htmlFor="ret-disposition">
            <Select
              id="ret-disposition"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value as RobotReturnDisposition)}
            >
              <option value="MAINTENANCE">정비 입고</option>
              {isAdmin && <option value="RETIRE">폐기</option>}
            </Select>
          </Field>
        )}

        {/* 개선 9건 차수1(26-08-04) 결정 ①A — 회수 장소. 선택지는 shared 라벨 맵을
         **순회해** 만든다(하드코딩 금지 — 값이 늘면 두 회수 화면이 함께 늘어난다). */}
        {showLocation && (
          <Field label="회수 장소" htmlFor="ret-location">
            <Select
              id="ret-location"
              value={returnLocation}
              onChange={(e) => setReturnLocation(e.target.value as RobotReturnLocation)}
            >
              {ROBOT_RETURN_LOCATION_CHOICES.map((loc) => (
                <option key={loc} value={loc}>
                  {ROBOT_RETURN_LOCATION_LABELS[loc]}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {isLastRobot && (
          <div style={lastRobotWarn} data-testid="last-robot-warning">
            이 고객사의 마지막 로봇입니다. 회수하면 고객사가 종료 처리됩니다.
          </div>
        )}

        <Field label="메모" htmlFor="ret-notes">
          <Input id="ret-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {/* 계획서(2026-08-27) 요구 3 — 회수 사진(선택). 서류 탭의 '회수 로봇 사진'
            카드가 같은 자리를 읽으므로, 지금 안 올려도 나중에 올릴 수 있다. */}
        <Field label="회수 사진 (선택)" htmlFor="ret-photos">
          <input
            id="ret-photos"
            type="file"
            accept="image/*"
            multiple
            data-testid="return-photo-input"
            onChange={(e) => setPhotos(Array.from(e.target.files ?? []))}
          />
          {photos.length > 0 && (
            <div className="kuun-caption" data-testid="return-photo-count">
              {photos.length}장을 골랐습니다. 회수를 저장한 뒤에 올라갑니다.
            </div>
          )}
        </Field>

        {photoError && (
          <div className="kuun-caption" data-testid="return-photo-error">
            {photoError}
          </div>
        )}
      </form>
    </Modal>
  );
}

const lastRobotWarn = {
  padding: '8px 10px',
  marginBottom: 14,
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  fontSize: 13,
  fontWeight: 600,
} as const;
